import {
  Modal,
  Button,
  FormField,
  SpaceBetween,
  TokenGroup,
  Box,
} from '@cloudscape-design/components';
import { useState, useEffect, useMemo } from 'react';
import { useAudits } from 'src/store/audits/useAudits';
import useUserProfile from 'src/hooks/useUserProfile';
import { toLower } from 'lodash';
import { ASSIGNEE_LABELS, ERROR_MESSAGES } from 'src/contstant/commonLabels';
import EmployeePicker from '../common/EmployeePicker';

const MAX_ASSIGNEES = 20;

interface ManageAssigneesModalProps {
  onSubmit: (assignees: string[]) => void;
  onDismiss: () => void;
  visible: boolean;
  currentAssignees: string[];
  auditId: string;
  businessUnitId: string;
}

function ManageAssigneesModal({
  currentAssignees,
  visible,
  onDismiss,
  onSubmit,
  auditId,
  businessUnitId,
}: ManageAssigneesModalProps) {
  const [inputValue, setInputValue] = useState('');
  const [assignees, setAssignees] = useState<string[]>([]);
  const [pendingAssignees, setPendingAssignees] = useState<string[] | null>(null);
  const [validationError, setValidationError] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const { updateAudit, updateAuditApiStatus, resetApiStatus } = useAudits();
  const { getUserId, getUserProgramId } = useUserProfile();

  // Sync local state when modal opens
  useEffect(() => {
    if (visible) {
      setAssignees([...currentAssignees]);
      setPendingAssignees(null);
      setInputValue('');
      setValidationError('');
      resetApiStatus();
      setIsProcessing(false);
    }
  }, [visible]);

  // Handle API response for add/remove operations
  useEffect(() => {
    if (!isProcessing) return;

    if (updateAuditApiStatus.isSuccess) {
      // Adopt the pending state as the new confirmed state
      if (pendingAssignees) {
        setAssignees(pendingAssignees);
        setPendingAssignees(null);
      }
      setIsProcessing(false);
      setValidationError('');
      resetApiStatus();
    }

    if (updateAuditApiStatus.errorMessage) {
      // Discard pending state, keep current assignees unchanged
      setPendingAssignees(null);
      setValidationError(updateAuditApiStatus.errorMessage);
      setIsProcessing(false);
      resetApiStatus();
    }
  }, [updateAuditApiStatus.isSuccess, updateAuditApiStatus.errorMessage]);

  // Check if current user is already in the assignees list
  const isCurrentUserAssigned = useMemo(
    () => assignees.some((a) => toLower(a) === toLower(getUserId())),
    [assignees, getUserId],
  );

  const handleAssignToMe = () => {
    if (isProcessing) return;
    const userId = toLower(getUserId());
    if (!isCurrentUserAssigned && assignees.length < MAX_ASSIGNEES) {
      const updatedAssignees = [...assignees, userId];
      setPendingAssignees(updatedAssignees);
      setIsProcessing(true);
      setInputValue('');
      updateAudit({
        auditId,
        assignees: updatedAssignees,
        businessUnitId,
        lastUpdatedBy: getUserId(),
      });
    }
  };

  const handleAddAssignee = () => {
    if (isProcessing) return;

    // EmployeePicker only commits a login that matched a PAPI suggestion,
    // so trimmed will be either a valid lowercase alias or empty.
    const trimmed = toLower(inputValue.trim());

    if (!trimmed) {
      setValidationError(ERROR_MESSAGES.ASSIGNEE_REQUIRED);
      return;
    }

    if (assignees.some((a) => toLower(a) === trimmed)) {
      setValidationError('This assignee has already been added');
      return;
    }

    if (assignees.length >= MAX_ASSIGNEES) {
      setValidationError(`Maximum of ${MAX_ASSIGNEES} assignees allowed`);
      return;
    }

    const updatedAssignees = [...assignees, trimmed];
    setPendingAssignees(updatedAssignees);
    setInputValue('');
    setValidationError('');
    setIsProcessing(true);
    updateAudit({
      auditId,
      assignees: updatedAssignees,
      businessUnitId,
      lastUpdatedBy: getUserId(),
    });
  };

  const handleRemoveAssignee = (index: number) => {
    if (isProcessing) return;
    const updatedAssignees = assignees.filter((_, i) => i !== index);
    setPendingAssignees(updatedAssignees);
    setIsProcessing(true);
    updateAudit({
      auditId,
      assignees: updatedAssignees,
      businessUnitId,
      lastUpdatedBy: getUserId(),
    });
  };

  const tokenItems = assignees.map((assignee, index) => ({
    label: assignee,
    dismissLabel: `Remove ${assignee}`,
    value: String(index),
  }));

  return (
    <Modal
      data-testid="manage-assignees-modal"
      visible={visible}
      onDismiss={() => {
        if (isProcessing) return;
        onSubmit(assignees);
        onDismiss();
      }}
      header="Manage Assignees"
      closeAriaLabel="Close modal"
    >
      <SpaceBetween direction="vertical" size="l" data-testid="modal-content">
        {assignees.length > 0 && (
          <FormField label="Current Assignees" data-testid="assignees-list-field">
            <TokenGroup
              data-testid="assignees-token-group"
              items={tokenItems}
              onDismiss={({ detail: { itemIndex } }) => handleRemoveAssignee(itemIndex)}
            />
          </FormField>
        )}

        {assignees.length === 0 && (
          <Box color="text-status-inactive" data-testid="no-assignees-message">
            No assignees assigned to this audit.
          </Box>
        )}

        {!isCurrentUserAssigned && assignees.length < MAX_ASSIGNEES && (
          <Button
            data-testid="assign-to-me-button"
            variant="normal"
            iconName="user-profile"
            onClick={handleAssignToMe}
            disabled={isProcessing}
          >
            Assign to me
          </Button>
        )}

        <FormField
          data-testid="add-assignee-field"
          label="Add Assignee"
          description={`Enter a valid user alias to add as an assignee (max ${MAX_ASSIGNEES})`}
          errorText={validationError}
        >
          <SpaceBetween direction="horizontal" size="xs">
            <EmployeePicker
              programId={getUserProgramId()}
              value={inputValue}
              data-testid="assignee-input"
              onSelect={(login) => {
                setInputValue(login);
                if (validationError) setValidationError('');
              }}
              onClear={() => setInputValue('')}
              placeholder={ASSIGNEE_LABELS.PLACEHOLDER}
              disabled={isProcessing}
              invalid={!!validationError}
            />
            <Button
              data-testid="add-assignee-button"
              onClick={handleAddAssignee}
              iconName="add-plus"
              disabled={!inputValue || assignees.length >= MAX_ASSIGNEES || isProcessing}
              loading={isProcessing}
            >
              Add
            </Button>
          </SpaceBetween>
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}

export default ManageAssigneesModal;
